import React, { useState, useEffect, useRef } from "react";
import {
  Home,
  TrendingUp,
  Users,
  BarChart3,
  Package,
  IndianRupee,
  Target,
  Clock,
  Building2,
} from "lucide-react";
import { useTheme } from "./context/themecontext";

import GmvMetricsSection from "./GmvMetricsSection";
import OrderAnalyticsSection from "./OrderAnalyticsSection";
import LeadsTable from "./leadsTable";
import SalesRepPerformancePanel from "./SalesRepPerformancePanel";
import LeadsByProjectTypeChart from "./LeadsByProjectTypeChart";
import EcommerceMetrics from "./infocards";





/* ---------------- SIMPLE UI COMPONENTS (NO EXTRA FILES) ---------------- */

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-1 py-1 md:px-3 md:py-2 rounded-md text-xs md:text-sm font-medium border  transition ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Badge = ({ children }) => (
  <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-600 text-white">
    {children}
  </span>
);

const Card = ({ children, onClick, isDarkMode }) => (
  <div
    onClick={onClick}
    className={`rounded-lg shadow p-4 hover:shadow-md cursor-pointer transition-colors duration-300 ${isDarkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'}`}
  >
    {children}
  </div>
);

const CardHeader = ({ children, className = "", isDarkMode }) => (
  <div className={`mb-2 flex items-center justify-between ${isDarkMode ? 'text-gray-100' : ''} ${className}`}>
    {children}
  </div>
);

// 🔹 Put this at the top of mdc.js (before CardTitle)

const InfoTooltip = ({ text, isDarkMode }) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = (e) => {
    e.stopPropagation(); // 🚀 THIS STOPS CARD CLICK
    setOpen(!open);
  };

  return (
    <div
      className="relative flex items-center ml-2"
      onClick={(e) => e.stopPropagation()} // extra safety
    >
      {/* Info Icon */}
      <div
        onClick={handleClick}
        className={`flex items-center justify-center 
          w-4 h-4 rounded-full 
          text-[10px] italic font-semibold 
          cursor-pointer transition-all duration-200
          ${
            isDarkMode
              ? "bg-gray-700 text-blue-400 hover:bg-blue-600 hover:text-white"
              : "bg-gray-200 text-blue-600 hover:bg-blue-600 hover:text-white"
          }`}
      >
        i
      </div>

      {/* Tooltip Popup */}
      {open && (
        <div
          className={`absolute left-7 top-1/2 -translate-y-1/2 
            w-56 px-3 py-2 rounded-lg shadow-xl text-xs 
            transition-all duration-200 z-50
            ${
              isDarkMode
                ? "bg-gray-800 text-gray-200 border border-gray-700"
                : "bg-white text-gray-700 border border-gray-200"
            }`}
        >
          {text}

          {/* Arrow */}
          <div
            className={`absolute left-[-6px] top-1/2 -translate-y-1/2 
              w-3 h-3 rotate-45 
              ${
                isDarkMode
                  ? "bg-gray-800 border-l border-t border-gray-700"
                  : "bg-white border-l border-t border-gray-200"
              }`}
          />
        </div>
      )}
    </div>
  );
};

const CardTitle = ({ children,tooltip, isDarkMode }) => (
  <div className="flex items-center">
  <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{children}</h3>
  {tooltip && (
      <InfoTooltip text={tooltip} isDarkMode={isDarkMode} />
    )}
  </div>  
);

const CardContent = ({ children, isDarkMode }) => <div className={isDarkMode ? 'text-gray-300' : ''}>{children}</div>;

/* ---------------- MAIN COMPONENT ---------------- */

function MDC() {
  const [activeView, setActiveView] = useState("dashboard");
  const [activeTab, setActiveTab] = useState("gmv");
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [dashboardData,setDashboardData] = useState();
  const [period,setPeriod] = useState('week');

  const leadsRef = useRef(null);
  const rfqRef = useRef(null);
  const salesRepRef = useRef(null);
  const productRef = useRef(null);
  const brandRef = useRef(null);
  const marketRef = useRef(null);

const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;  
const scrollAndHighlight = (ref) => {
  if (ref.current) {
    ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    ref.current.classList.add("highlight");
    setTimeout(() => ref.current?.classList.remove("highlight"), 1200);
  }
};

const handleInfoCardClick = (type) => {
  switch (type) {
    case "leads":
      scrollAndHighlight(leadsRef);
      break;
    case "rfq":
      scrollAndHighlight(rfqRef);
      break;
    case "salesrep":
      scrollAndHighlight(salesRepRef);
      break;
    case "product":
      scrollAndHighlight(productRef);
      break;
    case "brand":
      scrollAndHighlight(brandRef);
      break;
    case "market":
      scrollAndHighlight(marketRef);
      break;
    default:
      break;
  }
};

  useEffect(() => {
    fetchDashboardData(period);
  }, [period]);

const fetchDashboardData = async () => {
  try {
    const manufacturerId = sessionStorage.getItem("userId");
    console.log("Manufacturer ID:", manufacturerId);

    // ✅ Correct URL (no /brickbucket here)
    const apiUrl = `${fetchUrl}manufacturer/get-manufacturer-dashboard-data?manufacturerId=${manufacturerId}&period=${period}`;
    console.log("Fetching URL:", apiUrl);

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    console.log("Dashboard Data:", data);

    if (response.ok && data) {
      setDashboardData(data.data)
    } else {
      console.error(
        "Failed to fetch dashboard data:",
        data.message
      );
    }
  } catch (error) {
    console.error("Error in fetchDashboardData:", error);
  }
};

const handlePeriodChange = (e) => {
  setPeriod(e.target.value);
};


  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* HEADER */}
     <header className={`border-b sticky top-0 z-50 transition-colors duration-300 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
  <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
    <div className="flex items-center gap-4 md:gap-16">

      <nav className="flex flex-row md:flex-row gap-5 md:gap-16">
        {[
          { key: "dashboard", label: "Overview", icon: <Home className="h-4 w-4" /> },
          { key: "gmv", label: "GMV Analytics", icon: <TrendingUp className="h-4 w-4" /> },
          /* { key: "sales", label: "Sales & Leads", icon: <Users className="h-4 w-4" /> }, */
          { key: "orders", label: "Order Analytics", icon: <BarChart3 className="h-4 w-4" /> },
        ].map((item) => (
          <Button
            key={item.key}
            onClick={() => setActiveView(item.key)}
            className={`
              flex items-center gap-1
              px-3 py-2
              md:px-3 md:py-2
              text-sm md:text-base
              transition-colors duration-200
              ${
                activeView === item.key
                  ? "bg-blue-800 text-white"
                  : isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-black'
              }
            `}
          >
            {item.icon}
            <span>{item.label}</span>
          </Button>
        ))}
      </nav>

    </div>
  </div>
</header>


      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6">
        {activeView === "dashboard" && (
          <>
        <div className="mb-5 w-full">

          <div className="flex flex-row items-center justify-between gap-4">

            {/* LEFT: Title + Subtitle + Filter */}
            <div className="flex flex-col gap-2 min-w-0">

              <div>
                <h2
                  className={`text-sm sm:text-lg md:text-3xl font-bold truncate ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Brand Performance Overview
                </h2>
                <p
                  className={`text-xs sm:text-sm md:text-base truncate ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Digital Channel Metrics
                </p>
              </div>

              {/* Filter */}
              <select
                value={period}
                onChange={handlePeriodChange}
                className={`border rounded-md px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm self-start ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-200 border-gray-600'
                    : 'bg-white text-gray-900 border-gray-200'
                }`}
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>

            </div>

            {/* RIGHT: Image + Name */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0">

              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center">
                {dashboardData?.profile?.image ? (
                  <img
                    src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${dashboardData.profile.image}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/default-avatar.png';
                    }}
                  />
                ) : (
                  <span
                    className={`font-bold text-sm sm:text-base sm:font-bold ${
                      isDarkMode ? 'text-blue-400' : 'text-[#1A73E7]'
                    }`}
                  >
                    {dashboardData?.profile?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>

              <p
                className={`text-[10px] sm:text-sm font-medium max-w-[72px] sm:max-w-none truncate text-center ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}
              >
                {dashboardData?.profile?.name || ''}
              </p>

            </div>

          </div>
        </div>

            {/* METRICS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-6 mb-8">
              <Card /* onClick={() => setActiveView("gmv")} */ isDarkMode={isDarkMode}>
                <CardHeader isDarkMode={isDarkMode}>
                  <CardTitle
                    isDarkMode={isDarkMode}
                    tooltip="Gross Merchandise Value - Total sales value generated in selected period"
                  >
                    GMV
                  </CardTitle>
                  <IndianRupee className="h-4 w-4" />
                </CardHeader>
                <CardContent isDarkMode={isDarkMode}>
                  <div className={`text-lg md:text-2xl font-bold ${isDarkMode ? 'text-white' : ''}`}>₹{dashboardData?.gmv?.total_value}</div>
                  <p
                    className={`text-xs mt-1 ${
                      dashboardData?.gmv?.isPositive ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {dashboardData?.gmv?.isPositive ? "↑" : "↓"}{" "}
                    {dashboardData?.gmv?.change_percent} % from last {period}
                  </p>                
                </CardContent>
              </Card>

{/*               <Card onClick={() => setActiveView("sales")}>
                <CardHeader>
                  <CardTitle>Active Leads</CardTitle>
                  <Users className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-lg md:text-2xl font-bold">47</div>
                  <p className="text-xs text-green-600 mt-1">↑ 8 new this week</p>
                </CardContent>
              </Card> */}

              <Card /* onClick={() => setActiveView("orders")} */ isDarkMode={isDarkMode}>
                <CardHeader isDarkMode={isDarkMode}>
                  <CardTitle isDarkMode={isDarkMode}
                  tooltip="Total number of completed orders during selected period"
                  >Total Orders</CardTitle>
                  <Package className="h-4 w-4" />
                </CardHeader>
                <CardContent isDarkMode={isDarkMode}>
                  <div className="text-lg  md:text-2xl font-bold ">{dashboardData?.orders?.total_orders}</div>
                   <p
                    className={`text-xs mt-1 ${
                      dashboardData?.orders?.isPositive ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {dashboardData?.orders?.isPositive ? "↑" : "↓"}{" "}
                    {dashboardData?.orders?.change_percent} % from last {period}
                  </p>
                </CardContent>
              </Card>

              <Card isDarkMode={isDarkMode}>
                <CardHeader isDarkMode={isDarkMode}>
                  <CardTitle isDarkMode={isDarkMode}
                  tooltip="Percentage of RFQs converted into confirmed orders"
                  >Conversion Rate</CardTitle>
                  <Target className="h-4 w-4" />
                </CardHeader>
                <CardContent isDarkMode={isDarkMode}>
                  <div className="text-lg md:text-2xl font-bold">{dashboardData?.conversion_rate?.value} %</div>
                   <p
                    className={`text-xs mt-1 ${
                      dashboardData?.conversion_rate?.isPositive ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {dashboardData?.conversion_rate?.isPositive ? "↑" : "↓"}{" "}
                    {dashboardData?.conversion_rate?.change_percent} % from last {period}
                  </p>
                </CardContent>
              </Card>

{/*               <Card>
                <CardHeader>
                  <CardTitle>
                   Top Region by GMV
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-md md:text-xl font-bold">East Region</div>
                  <p className="text-sm text-gray-600 mt-1">$520,000 GMV</p>
                </CardContent>
              </Card> */}

               <Card isDarkMode={isDarkMode}>
                <CardHeader isDarkMode={isDarkMode}>
                  <CardTitle isDarkMode={isDarkMode}
                  tooltip="Channel partner with highest GMV and performance"
                  >
                    Top Performing Channel Partner
                  </CardTitle>
                  <Building2 className="h-4 w-4" />
                </CardHeader>
                <CardContent isDarkMode={isDarkMode}>
                  <div className="text-md md:text-xl font-bold">{dashboardData?.top_performing_partner?.name}</div>
                  <p className="text-sm mt-1 font-semibold">Orders: {dashboardData?.top_performing_partner?.total_orders}</p>
                  <p className="text-sm mt-1 font-semibold">GMV: ₹ {dashboardData?.top_performing_partner?.total_value}</p>
                  <p
                    className={`text-xs mt-1 ${
                      dashboardData?.top_performing_partner?.conversion_rate?.isPositive ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {dashboardData?.top_performing_partner?.conversion_rate?.isPositive ? "↑" : "↓"}{" "}
                    {dashboardData?.top_performing_partner?.conversion_rate?.value} % from last {period}
                  </p>
                </CardContent>
              </Card>

               <Card isDarkMode={isDarkMode}>
                <CardHeader isDarkMode={isDarkMode}>
                  <CardTitle isDarkMode={isDarkMode}
                  tooltip="Average time taken to respond to RFQs"
                  >
                    Channel Partner Response Time
                  </CardTitle>
                  <Clock className="h-4 w-4" />
                </CardHeader>
                <CardContent isDarkMode={isDarkMode}>
                <div className="text-md md:text-xl font-bold">{dashboardData?.avg_response_time?.time} hours</div>
                  <p
                    className={`text-xs mt-1 ${
                      dashboardData?.avg_response_time?.isPositive ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {dashboardData?.avg_response_time?.isPositive ? "↑" : "↓"}{" "}
                    {dashboardData?.avg_response_time?.change_percent} % from last {period}
                  </p>
                </CardContent>
              </Card>
            </div>

{/* TABS CONTAINER */}
<div className="space-y-2 md:space-y-6">
  {/* TAB HEADER */}
  <div className={`inline-flex rounded-full p-1 gap-1 md:gap-2 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-100 border border-gray-200'}`} >
    {[
      /* { key: "leads", label: "Leads & RFQ" }, */
      { key: "gmv", label: "GMV Overview" },
      /* { key: "sales", label: "Sales Performance" }, */
      { key: "orders", label: "Order Analytics" },
    ].map((tab) => (
      <button
        key={tab.key}
        onClick={() => setActiveTab(tab.key)}
        className={`
          px-3 py-1 md:px-6 md:py-2
          text-xs md:text-sm
          font-medium
          rounded-full
          transition-all
          ${
            activeTab === tab.key
              ? (isDarkMode ? "bg-gray-700 text-white shadow" : "bg-white text-black shadow")
              : (isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-black")
          }
        `}
      >
        {tab.label}
      </button>
    ))}
  </div>

  {/* TAB CONTENT */}
{/*   {activeTab === "leads" && (
    <div className="space-y-4 md:space-y-6">
     <EcommerceMetrics onCardClick={handleInfoCardClick} />
      <LeadsByProjectTypeChart />
    </div>
  )} */}

  {activeTab === "gmv" && (
    <div className="space-y-4 md:space-y-6">
      <GmvMetricsSection />
    </div>
  )}

{/*   {activeTab === "sales" && (
    <div className="space-y-4 md:space-y-6">
      <LeadsByProjectTypeChart />
      <SalesRepPerformancePanel />
    </div>
  )} */}

  {activeTab === "orders" && (
    <div className="space-y-4 md:space-y-6">
      <OrderAnalyticsSection />
    </div>
  )}
</div>



          </>
        )}

      {/* GMV VIEW */}
{activeView === "gmv" && (
  <div className="space-y-4 md:space-y-6">
    <div>
      <Button
        onClick={() => setActiveView("dashboard")}
        className={`${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white'}`}
      >
        ← Back to Overview
      </Button>
    </div>
    <GmvMetricsSection />
  </div>
)}

{/* SALES & LEADS VIEW */}
{/* {activeView === "sales" && (
  <div className="space-y-4 md:space-y-6">
    <div>
      <Button
        onClick={() => setActiveView("dashboard")}
        className="bg-white "
      >
        ← Back to Overview
      </Button>
    </div>
       <EcommerceMetrics onCardClick={handleInfoCardClick} />
    <LeadsByProjectTypeChart/>
    <SalesRepPerformancePanel />
    <LeadsTable />
    
  </div>
)} */}

{/* ORDER ANALYTICS VIEW */}
{activeView === "orders" && (
  <div className="space-y-4 md:space-y-6">
    <div>
      <Button
        onClick={() => setActiveView("dashboard")}
        className={`${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white'}`}
      >
        ← Back to Overview
      </Button>
    </div>
    <OrderAnalyticsSection />
  </div>
)}

      </main>
    </div>
  );
}

export default MDC;
